import { Column, Index, PrimaryGeneratedColumn } from 'typeorm';
import { Entity } from '../../decorators';

/**
 * Stores periodic compliance KPI snapshots for dashboard display and trend analysis.
 *
 * - Rows with snapshot_date = NULL are "live" refreshes (latest KPI state, every 15 min)
 * - Rows with snapshot_date set are daily snapshots for 90-day trend sparklines
 * - The REFRESH_COMPLIANCE_STATS job on the LICENSE_COMPLIANCE_SNAPSHOT queue populates this
 */
@Index('idx_snapshot_date', ['snapshotDate'], { unique: true })
@Index('idx_computed_at', ['computedAt'])
@Entity('license_compliance_snapshot', {}, { auditable: false })
export class LicenseComplianceSnapshotEntity {
	@PrimaryGeneratedColumn({ name: '_id', unsigned: true })
	public id!: number;

	@Column('date', { name: 'snapshot_date', nullable: true })
	public snapshotDate!: string | null;

	@Column('int', { name: 'pending_match', default: 0 })
	public pendingMatch!: number;

	@Column('int', { name: 'expiring_soon', default: 0 })
	public expiringSoon!: number;

	@Column('int', { name: 'expired', default: 0 })
	public expired!: number;

	@Column('int', { name: 'at_risk', default: 0 })
	public atRisk!: number;

	@Column('int', { name: 'recently_changed', default: 0 })
	public recentlyChanged!: number;

	@Column('int', { name: 'total_licenses', default: 0 })
	public totalLicenses!: number;

	@Column('int', { name: 'matched_to_registry', default: 0 })
	public matchedToRegistry!: number;

	/**
	 * Bad Standing KPI (PD-13949): customer licenses matched to a Cancelled/
	 * Revoked/Suspended registry license. Nullable so snapshots written before
	 * this column existed read as "not available" and the dashboard falls back
	 * to a live count (same transition pattern as the grade_* columns).
	 */
	@Column('int', { name: 'bad_standing', nullable: true })
	public badStanding!: number | null;

	@Column('char', { name: 'health_score', length: 1, nullable: true })
	public healthScore!: string | null;

	/**
	 * Grade inputs (PD-14395): the demand-based client-coverage counts the
	 * letter grade was computed from — denominator = active Customer clients
	 * with a sale in the last 90 days, numerator = those with >=1 matched,
	 * non-expired license. Persisted so the dashboard can display the NUMERIC
	 * score from the SAME formula as the letter (the ring previously showed a
	 * license-row match % next to a client-coverage letter). Nullable so
	 * snapshots written before this column existed read as "not available"
	 * and the dashboard falls back to computing the counts live.
	 */
	@Column('int', { name: 'grade_total_clients', nullable: true })
	public gradeTotalClients!: number | null;

	@Column('int', { name: 'grade_matched_clients', nullable: true })
	public gradeMatchedClients!: number | null;

	@Column('datetime', {
		name: 'computed_at',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public computedAt!: Date;

	@Column('datetime', {
		name: '_created_date',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public createdDate!: Date;
}
